import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { usePropertyOptions } from '../../lib/options';
import { Modal, Button, Field, Input, Select, Textarea } from '../ui';

const TEAMS = [
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'lettings', label: 'Lettings' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'directors', label: 'Directors' },
] as const;

interface UserLite { id: string; email: string; fullName: string | null }

/**
 * "Add something to the list" (prototype §36): anything the portal cannot work out for itself — what, by when, how
 * urgent, whose, department, building, note. It then sits on My Day beside the generated tasks and is ticked off,
 * snoozed or handed over the same way.
 */
export function AddTask({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { user, can } = useAuth();
  const canPick = can('users.read');
  const props = usePropertyOptions();
  const people = useQuery({
    queryKey: ['/users', 'my-day-owners'],
    queryFn: async () => (await api.get<UserLite[]>('/users', { isActive: true, perPage: 200, sort: 'first_name' })).data,
    enabled: canPick,
    staleTime: 5 * 60_000,
  });
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [urgency, setUrgency] = useState<'NORMAL' | 'NOW'>('NORMAL');
  const [owner, setOwner] = useState<string>('me');
  const [team, setTeam] = useState<string>('maintenance');
  const [propertyId, setPropertyId] = useState('');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () => api.post('/my-day/tasks', {
      title: title.trim(), dueDate: due || null, urgency, team, propertyId: propertyId || null, note: note.trim() || null,
      ...(owner === 'anyone' ? { anyone: true } : owner === 'me' ? {} : { ownerId: owner }),
    }),
    onSuccess: onDone,
  });

  return (
    <Modal open onClose={onClose} title="Add something to the list"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button icon="Plus" onClick={() => save.mutate()} disabled={!title.trim() || save.isPending}>{save.isPending ? 'Adding…' : 'Add to My Day'}</Button></>}>
      <p className="mb-4 text-[12.5px] text-ink-500">Anything the portal cannot work out for itself.</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="What needs doing"><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="w-full" placeholder="Ring the council about the bin collection" autoFocus /></Field></div>
        <Field label="By when"><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-full" /></Field>
        <Field label="How urgent">
          <Select value={urgency} onChange={(e) => setUrgency(e.target.value as 'NORMAL' | 'NOW')} className="w-full">
            <option value="NOW">Now</option>
            <option value="NORMAL">When you can</option>
          </Select>
        </Field>
        <Field label="Whose">
          <Select value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full">
            <option value="me">Me ({user?.firstName ?? user?.email})</option>
            <option value="anyone">Anyone on the team</option>
            {(people.data ?? []).filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.fullName ?? u.email}</option>)}
          </Select>
        </Field>
        <Field label="Department">
          <Select value={team} onChange={(e) => setTeam(e.target.value)} className="w-full">
            {TEAMS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Building (optional)">
            <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-full">
              <option value="">Not about one building</option>
              {((props.data ?? []) as Array<{ id: string; name: string }>).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-2"><Field label="Note"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full" placeholder="Anything they need to know" /></Field></div>
      </div>
      {save.error && <p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{(save.error as Error).message}</p>}
    </Modal>
  );
}
